import { storyblokEditable } from '@storyblok/react/rsc';
import { resolveLink, colorStyle } from '@/lib/helpers';
import PhosphorIcon from '@/components/PhosphorIcon';
import TestimonialCarouselClient from '@/components/TestimonialCarouselClient';

const GoogleG = ({ size = 20 }) => (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true" className="shrink-0">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.28-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

const Stars = ({ count }) => {
  const safe = Math.max(0, Math.min(5, Math.round(count * 2) / 2));
  const full = Math.floor(safe);
  const hasHalf = safe - full >= 0.5;
  return (
    <div className="flex gap-0.5 shrink-0 text-yellow" role="img" aria-label={`${safe} out of 5 stars`}>
      {Array.from({ length: full }, (_, i) => (
        <PhosphorIcon key={`f${i}`} name="Star" size={18} weight="fill" />
      ))}
      {hasHalf && <PhosphorIcon name="StarHalf" size={18} weight="fill" />}
    </div>
  );
};

const TestimonialCarousel = ({ blok }) => {
  const cards = blok.cards || [];

  // w-[82vw] leaves the next card peeking at the edge on a phone, which is the
  // only cue that the row scrolls; it caps at 350px so it never grows past the
  // desktop card. select-none keeps a mouse drag from selecting the quote text.
  const cardClasses =
    'flex-shrink-0 w-[82vw] max-w-[350px] sm:w-[350px] bg-white text-black rounded-md p-8 shadow-card flex flex-col gap-4 select-none';
  // Cards are white unless the editor picks a Card colour (e.g. light purple
  // cards on a white section, as the old Leap page had).
  const cardStyle = blok.card_color ? { backgroundColor: blok.card_color } : undefined;

  return (
    <section {...storyblokEditable(blok)} className="py-10 sm:py-16 md:py-20" style={colorStyle(blok)}>
      {blok.title && (
        <h2 className="text-32 sm:text-36 md:text-48 font-extrabold text-center mb-10 w-full max-w-container mx-auto px-4 md:px-8">
          {blok.title}
        </h2>
      )}
      {/* The row scrolls inside the side margins rather than out to the edge, so
          a card cut off on the right stops at the same margin as the left. */}
      <div className="mx-4 sm:mx-8 md:mx-12 overflow-hidden">
      <TestimonialCarouselClient
        label={blok.title || 'Testimonials'}
        loop={blok.loop === true}
        className="flex gap-4 sm:gap-8 overflow-x-auto px-1 pb-6"
      >
        {cards.map((card) => {
          const href = resolveLink(card.link);
          const inner = (
            <>
              <PhosphorIcon name="Quotes" size={48} />
              {card.quote && <p className="text-14 italic flex-1">{card.quote}</p>}
              <div className="flex items-center justify-between gap-3 mt-auto pt-4 border-t border-gray-border">
                {card.author && <p className="text-14 font-bold min-w-0">{card.author}</p>}
                  <div className="flex items-center gap-3">
                      <GoogleG />
                      <Stars count={Number(card.rating)} />
                  </div>
              </div>
            </>
          );

          return href ? (
            <a
              key={card._uid}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${cardClasses} transition-shadow hover:shadow-lg`}
              style={cardStyle}
              draggable={false}
              {...storyblokEditable(card)}
            >
              {inner}
            </a>
          ) : (
            <div key={card._uid} className={cardClasses} style={cardStyle} {...storyblokEditable(card)}>
              {inner}
            </div>
          );
        })}
      </TestimonialCarouselClient>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
